/**
 * This is the starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import React, { useEffect } from 'react';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { MainContainer, Container } from './FeatureApp.styles';

import {
  DynamicFinancingValues,
  useStockContext,
  CarinfoWrapper,
  OneCMSLayoutWrapper,
  useServicesContext,
  useDynamicFinancing,
  extractAndPersistFiltersFromHash,
} from '@oneaudi/vtp-shared';

import StageWrapper from './components/stage/subcomponents/StageWrapper';
import { ConversionBar } from './components/conversionBar/subcomponents/ConversionBar';
import { StageContextProvider } from './components/stage/hooks/StageContext';
import { trackFeatureAppReady } from './components/tracking/Tracking';
import { NextBestActions } from './components/nextBestActions/NextBestActions';
import { TechnicalData } from './components/technicalData/TechnicalDataPdp';
import { Equipments } from './components/equipments/Equipments';
import Warranties from './components/warranties/Warranties';

const FeatureApp: React.FC<{}> = () => {
  const { vehicle } = useStockContext();

  if (!vehicle) {
    return null;
  }

  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service'
  ) as TrackingServiceV2;

  const video = vehicle?.detail?.videos?.external?.youtube?.href;

  const { getConfiguration, getEnvironmentConfig } = useServicesContext();
  const vtpConfiguration = getConfiguration();
  const envConfig = getEnvironmentConfig();

  const [modifiedDynamicFinancingValues, setModifiedDynamicFinancingValues] =
    React.useState<DynamicFinancingValues>();
  const [render, setRender] = React.useState<boolean>();

  useEffect(() => {
    trackFeatureAppReady(trackingService, vehicle.basic!);

    // check for share url with filter hash
    if (vtpConfiguration && envConfig) {
      extractAndPersistFiltersFromHash(vtpConfiguration, envConfig);
    }
  }, []);

  // Resets the tabIndex on scrollToTop click.
  function focusFirstVisible(selectors: string) {
    if (typeof window !== 'undefined') {
      const candidates = Array.from(document.querySelectorAll<HTMLElement>(selectors));

      for (const el of candidates) {
        const styles = getComputedStyle(el);

        const isVisible =
          el.offsetParent !== null && styles.visibility !== 'hidden' && styles.opacity !== '0';

        if (isVisible) {
          el.focus();
          if (document.activeElement === el) {
            return;
          }
        }
      }
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY === 0) {
        focusFirstVisible('a, button, [tabindex]');
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Components are shared between the PDP and the Feature App
  const dynamicFinancingValues = vehicle.basic?.financing && useDynamicFinancing({ vehicle });

  const updateDynamicFinancingValues = (values?: DynamicFinancingValues) => {
    if (vehicle.basic.financing && dynamicFinancingValues && values) {
      const updatedValues = { ...values };
      setModifiedDynamicFinancingValues(updatedValues);
      setRender(false);
    }
  };

  useEffect(() => {
    if (dynamicFinancingValues && dynamicFinancingValues.rate && !render) {
      setModifiedDynamicFinancingValues(dynamicFinancingValues);
      setRender(true);
    }
  }, [dynamicFinancingValues, render]);

  return (
    <OneCMSLayoutWrapper useUnifiedLibrary>
      <MainContainer>
        <StageContextProvider videoUrl={video}>
          <Container className="pdp-stage">
            <StageWrapper data-testid="pdp-stage-wrapper" vehicleRaw={vehicle} />
          </Container>
        </StageContextProvider>
        <Container className="pdp-carinfo">
          <CarinfoWrapper
            vehicle={vehicle}
            dynamicFinancingValues={modifiedDynamicFinancingValues}
            updateDynamicFinancingValues={updateDynamicFinancingValues}
          />
        </Container>
        <Container className="nba">
          <NextBestActions vehicleId={vehicle.basic.id} />
        </Container>
        <Equipments vehicle={vehicle} />
        <TechnicalData vehicleRaw={vehicle} />
        <Warranties vehicle={vehicle} />
      </MainContainer>
      <ConversionBar
        vehicleRaw={vehicle}
        dynamicFinancingValues={modifiedDynamicFinancingValues}
        updateDynamicFinancingValues={updateDynamicFinancingValues}
      />
    </OneCMSLayoutWrapper>
  );
};

export default FeatureApp;
